import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type AutoPlanRunStatus = "preview" | "committed" | "reverted" | "failed";

export type AutoPlanLessonType = "normal" | "special" | "extra";

export type AutoPlanInstructorPriority = "full_time_first" | "balanced";

export type PlannedAppointment = {
  student_id: string;
  student_name: string;
  instructor_id: string;
  instructor_name: string;
  vehicle_id: string;
  plate_no: string;
  starts_at: string;
  ends_at: string;
  lesson_index: number;
  lesson_type: AutoPlanLessonType;
  date: string;
};

export type UnscheduledItem = {
  student_id: string;
  student_name: string;
  reason: "no_available_slot" | "student_not_active" | "no_instructor_pool" | "no_vehicle_pool";
  scheduled_count: number;
  requested_count: number;
};

export type RunSummaryCounts = {
  total_students?: number;
  active_students?: number;
  inactive_students?: number;
  total_requested: number;
  total_planned: number;
  total_unscheduled: number;
  inserted_count?: number;
  cancelled_count?: number;
};

export type PreviewRequest = {
  branchId?: string;
  studentIds: string[];
  startDate: string;
  endDate: string;
  lessonsPerStudent: number;
  lessonType?: AutoPlanLessonType;
  instructorPriority?: AutoPlanInstructorPriority;
  excludeWeekdays?: number[];
  preferredInstructorId?: string;
  preferredVehicleId?: string;
  maxLessonsPerStudentPerDay?: number;
};

export type PreviewResponse = {
  run_id: string;
  status: "preview";
  branch_id: string;
  created_at: string;
  expires_at: string;
  parameters: PreviewRequest;
  summary: RunSummaryCounts;
  planned: PlannedAppointment[];
  unscheduled: UnscheduledItem[];
};

export type CommitResponse = {
  committed: boolean;
  run_id: string;
  status: "committed";
  inserted_count: number;
  appointment_ids: string[];
};

export type RevertResponse = {
  reverted: boolean;
  run_id: string;
  cancelled_count: number;
  reason: string | null;
};

export type RunSummary = {
  run_id: string;
  status: AutoPlanRunStatus;
  branch_id: string;
  total_requested: number;
  total_planned: number;
  total_unscheduled: number;
  inserted_count: number;
  cancelled_count: number;
  created_at: string;
  committed_at: string | null;
  reverted_at: string | null;
  expires_at: string;
};

export type RunListResponse = {
  items: RunSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type RunDetail = {
  run_id: string;
  status: AutoPlanRunStatus;
  branch_id: string;
  created_at: string;
  expires_at: string;
  committed_at: string | null;
  reverted_at: string | null;
  summary: RunSummaryCounts;
  parameters: PreviewRequest;
  planned: PlannedAppointment[];
  unscheduled: UnscheduledItem[];
};

export async function previewAutoPlan(input: PreviewRequest): Promise<PreviewResponse> {
  return api.post<PreviewResponse>("/auto-planning/preview", input);
}

export async function commitAutoPlan(runId: string): Promise<CommitResponse> {
  return api.post<CommitResponse>("/auto-planning/commit", { runId });
}

export async function revertAutoPlan(runId: string, reason?: string): Promise<RevertResponse> {
  return api.post<RevertResponse>("/auto-planning/revert", { runId, reason });
}

export async function fetchRunDetail(runId: string): Promise<RunDetail> {
  return api.get<RunDetail>(`/auto-planning/runs/${runId}`);
}

export type RunListFilter = {
  status?: AutoPlanRunStatus | "";
  branchId?: string;
  limit?: number;
  offset?: number;
};

export type RunListState = {
  data: RunListResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useAutoPlanRunList(filter: RunListFilter): RunListState {
  const [data, setData] = useState<RunListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { status, branchId, limit, offset } = filter;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<RunListResponse>(
        "/auto-planning/runs",
        {
          status: status || undefined,
          branch_id: branchId || undefined,
          limit: limit ?? 20,
          offset: offset ?? 0,
        },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Plan gecmisi Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, branchId, limit, offset, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, reload };
}
